import React, { useEffect, useRef, useState } from 'react';
import queryString from 'query-string';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';

import './Home.css';
import logo from '../logo-bw.png';
import Header from '../components/Header';
import Mapbox from '../components/Mapbox';

import {
  UPDATED_RECENTLY_SECS,
  UPDATED_VERY_RECENTLY_SECS,
  POLL_DURATION_SECS,
  POLL_LIMIT
} from '../consts';

const getServiceId = service => `${service.service_id}-${service.vehicle}`;
const getRecentTime = (seconds) => moment().subtract(seconds, 'seconds').toISOString();

const Footer = () => (
  <div className='footer' style={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'right'}}>
    <div>Powered by </div>
    <div><a href="https://shuttleid.uk" target="_blank" rel="noreferrer"><img className='logo' src={logo} alt="ShuttleID" /></a></div>
  </div>
);

const Failure = ({ message, lastUpdated }) => (
  <div className='container'>
    <div className='content'>
      <Header lastUpdated={lastUpdated} />

      <div className="map-message">
        {message}
      </div>

      <Footer />
    </div>
  </div>
);

const VehicleTracker = ({ location }) => {
  const { id: clientId } = useParams();
  const [apiKey, setApiKey] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [lastUpdated, setLastUpdated] = useState();
  const [error, setError] = useState();
  const [pollCount, setPollCount] = useState(0);
  const pollInterval = useRef(0);

  useEffect(() => {
    const servicesUrl = process.env.REACT_APP_TRACKING_API_ENDPOINT +
      ['/', 'live-tracking', 'client', clientId, 'public'].join('/');

    const getTrackingData = async () => {
      try {
        const { data } = await axios.get(servicesUrl);

        const {
          api_key,
          last_updated,
          services: newServices
        } = data;

        setApiKey(api_key);

        const lastUpdated = [
          last_updated,
          ...newServices.map(s => s.last_updated)
        ].sort().pop();

        if (lastUpdated) {
          setLastUpdated(lastUpdated);
        }

        setServices((services) => {
          const servicesToAdd = newServices.filter(service => !services.find(
            s => getServiceId(service) === getServiceId(s)
          ));

          const servicesToUpdate = services.filter(service => newServices.find(
            s => getServiceId(service) === getServiceId(s)
          ));

          return [
            ...servicesToAdd,
            ...servicesToUpdate.map((service) => {
              const serviceUpdate = newServices.find(
                s => getServiceId(service) === getServiceId(s)
              );

              if (!serviceUpdate) {
                return service;
              }

              const isNewer = serviceUpdate.last_updated > service.last_updated;
              return isNewer ? serviceUpdate : service;
            })
          ].filter(
            ({ last_updated }) => last_updated > getRecentTime(UPDATED_RECENTLY_SECS)
          );
        });

        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setPollCount(count => count + 1);
        setIsLoading(false);
      }
    };

    getTrackingData();

    pollInterval.current = setInterval(getTrackingData, POLL_DURATION_SECS);
    return () => clearInterval(pollInterval.current);
  }, [clientId])

  useEffect(() => {
    if (pollCount >= POLL_LIMIT) {
      clearInterval(pollInterval.current);
    }
  }, [pollCount])

  const servicesUpdatedRecently = services.filter(
    s => s.last_updated >= getRecentTime(UPDATED_RECENTLY_SECS) &&
      s.last_updated < getRecentTime(UPDATED_VERY_RECENTLY_SECS)
  );

  const servicesUpdatedVeryRecently = services.filter(
    s => s.last_updated >= getRecentTime(UPDATED_VERY_RECENTLY_SECS)
  );

  const hasRecentUpdates = servicesUpdatedRecently.length || servicesUpdatedVeryRecently.length;

  if (error && !error.response) {
    return <Failure message={
      <>
        <h4>Tracking failed to load</h4>
        <p>An issue was encountered when trying to load vehicle tracking.</p>
        <p>Please make sure your network connection is stable and try again later.</p>
      </>
    } lastUpdated={lastUpdated} />;
  }

  if (error?.response?.status >= 400 || (!isLoading && !services.length)) {
    return <Failure message={
      <>
        <h4>Tracking unavailable</h4>
        <p>Vehicle tracking is currently unavailable.</p>
        <p>Please try this service again later.</p>
      </>
    } lastUpdated={lastUpdated} />;
  }

  if (services.length && !hasRecentUpdates) {
    return <Failure message={
      <>
        <h4>Tracking unavailable</h4>
        <p>Vehicle tracking is currently unavailable.</p>
        <p>Please try this service again later.</p>
      </>
    } lastUpdated={lastUpdated} />;
  }

  if (pollCount >= POLL_LIMIT) {
    return <Failure message={(
      <>
        <h4>Tracking disabled</h4>
        <p>Tracking disabled due to user inactivity.</p>
        <p>Need to continue tracking? Hit refresh in your web browser.</p>
      </>
    )} lastUpdated={lastUpdated} />;
  }

  if (isLoading) {
    return (
      <>
        <Header />
        <div style={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <div className="spinner">
            <div className="bounce1"></div>
            <div className="bounce2"></div>
            <div className="bounce3"></div>
          </div>
          <p>Locating vehicles...</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
      <div className='container'>
        <div className='content'>
          <Header lastUpdated={lastUpdated} isTrackingActive={hasRecentUpdates} />

          <Mapbox
            apiKey={apiKey}
            services={services}
            lessRecentUpdates={servicesUpdatedRecently.length}
          />

          <Footer />
        </div>
      </div>
  );
};

export default VehicleTracker;
